import {
  Box,
  IconButton,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Menu,
  MenuItem,
  Select,
} from "@mui/material";
import AddIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import { useId, useRef, useState, ReactNode } from "react";

import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import Help from "~/components/common/Help";

type Props = {
  emptyLabel: string;
  items: {
    key: number;
    label: ReactNode;
  }[];

  addOptions: {
    key: number;
    label: string;
  }[];

  addLabel: string;
  onAdd: (key: number[]) => void | Promise<any>;
  addLoading?: boolean;
  addMultiple?: boolean;
  addOnSelect?: boolean;
  dense?: boolean;
  disablePadding?: boolean;
  itemsMaxHeight?: number | string;
  itemsMinHeight?: number | string;

  onRemove: (key: number) => void | Promise<any>;
  removeLoading?: boolean;
  removeHelpText?: string;
};

// Component for "add on select" mode where item is added immediately when selected
const AddOnSelectControl = ({ id, addLabel, addOptions, selectedAddKey, onAdd, addLoading, removeLoading }: {
  id: string;
  addLabel: string;
  addOptions: Props["addOptions"];
  selectedAddKey: number[];
  onAdd: Props["onAdd"];
  addLoading?: boolean;
  removeLoading?: boolean;
}) => {
  const [localSelection, setLocalSelection] = useState<number[]>(selectedAddKey);

  return (
    <ListItem>
      <FormControl variant="outlined" size="small">
        <InputLabel htmlFor={id}>{addLabel}</InputLabel>
        <Select
          id={id}
          value={localSelection.map(key => key.toString())}
          sx={{ minWidth: "18em" }}
          onChange={async (e) => {
            const value = Number(e.target.value);
            setLocalSelection([value]);
            await onAdd([value]);
            setLocalSelection([]);
          }}
          inputProps={{ "data-testid": "add-dropdown" }}
          aria-label={addLabel}
          label={addLabel}
          disabled={removeLoading || addLoading}
        >
          <MenuItem value="" disabled></MenuItem>
          {addOptions.map((option) => (
            <MenuItem key={option.key} value={option.key.toString()}>
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ListItem>
  );
};

// Component for manual add mode with button
const AddWithButtonControl = ({ id, addLabel, addOptions, addMultiple, selectedAddKey, setSelectedAddKey, onAdd, addLoading, removeLoading }: {
  id: string;
  addLabel: string;
  addOptions: Props["addOptions"];
  addMultiple?: boolean;
  selectedAddKey: number[];
  setSelectedAddKey: (keys: number[]) => void;
  onAdd: Props["onAdd"];
  addLoading?: boolean;
  removeLoading?: boolean;
}) => (
  <ListItem
    sx={{ ".MuiListItemSecondaryAction-root": { minWidth: 40 } }}
    secondaryAction={addLoading ?
      <CircularProgress size={24} />
    :
      <IconButton
        edge="end"
        aria-label="Add"
        onClick={async () => {
          await onAdd(selectedAddKey);
          setSelectedAddKey([]);
        }}
        disabled={removeLoading || !selectedAddKey.length}
      >
        <AddIcon />
      </IconButton>
    }
  >
    <FormControl variant="outlined" size="small">
      <InputLabel htmlFor={id}>{addLabel}</InputLabel>
      {addMultiple ? (
        <CheckboxSelect
          id={id}
          value={selectedAddKey}
          onChange={setSelectedAddKey}
          options={addOptions}
          sx={{ minWidth: "18em" }}
          aria-label={addLabel}
          label={addLabel}
        />
      ) : (
        <Select
          id={id}
          value={selectedAddKey.map(key => key.toString())}
          sx={{ minWidth: "18em" }}
          onChange={e => setSelectedAddKey([Number(e.target.value)])}
          inputProps={{ "data-testid": "add-dropdown" }}
          aria-label={addLabel}
          label={addLabel}
        >
          <MenuItem value="" disabled></MenuItem>
          {addOptions.map((option) => (
            <MenuItem key={option.key} value={option.key.toString()}>
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  </ListItem>
);

const AddRemoveList = ({ emptyLabel, items, addOptions, addLabel, onAdd, addLoading, addMultiple, addOnSelect, dense, disablePadding, itemsMaxHeight, itemsMinHeight, onRemove, removeLoading, removeHelpText}: Props) => {
  const [selectedAddKey, setSelectedAddKey] = useState<number[]>([]);
  const [removeOpen, setRemoveOpen] = useState<null | [number, HTMLElement]>(null);
  const id = useId();
  const listRef = useRef<HTMLUListElement | null>(null);

  return <Box>

    <List
      ref={listRef}
      dense={dense}
      disablePadding={disablePadding}
      sx={itemsMaxHeight || itemsMinHeight ? {
        maxHeight: itemsMaxHeight,
        minHeight: itemsMinHeight,
        overflowY: "auto",
      } : undefined}
    >
      {items.length === 0 && <ListItem>{emptyLabel}</ListItem>}
      {items.map(item => (
        <ListItem
          key={item.key}
          secondaryAction={<IconButton aria-label="remove" data-testid="remove-button" onClick={(e) => setRemoveOpen([item.key, e.currentTarget])}>
            <RemoveIcon />
          </IconButton>}
        >
          {item.label}
        </ListItem>
      ))}
    </List>

    {addOptions.length > 0 && <List dense={dense} disablePadding={disablePadding} sx={{ mt: 1 }}>
      {addOnSelect && !addMultiple ? (
        <AddOnSelectControl
          id={id}
          addLabel={addLabel}
          addOptions={addOptions}
          selectedAddKey={selectedAddKey}
          onAdd={onAdd}
          addLoading={addLoading}
          removeLoading={removeLoading}
        />
      ) : (
        <AddWithButtonControl
          id={id}
          addLabel={addLabel}
          addOptions={addOptions}
          addMultiple={addMultiple}
          selectedAddKey={selectedAddKey}
          setSelectedAddKey={setSelectedAddKey}
          onAdd={onAdd}
          addLoading={addLoading}
          removeLoading={removeLoading}
        />
      )}
    </List>}

    <Menu open={!!removeOpen} onClose={() => setRemoveOpen(null)} anchorEl={removeOpen && removeOpen[1]}>
      <MenuItem
        onClick={async () => {
          if (!removeOpen) { return }
          await onRemove(removeOpen[0])
          setRemoveOpen(null)
        }}
        disabled={removeLoading || addLoading}
      >
        Confirm Remove
        {removeLoading ?
          <CircularProgress size={24} sx={{ ml: 0.5 }} />
        :
          removeHelpText && <Help text={removeHelpText} />
        }
      </MenuItem>
    </Menu>
  </Box>
}

export default AddRemoveList
